import { useId } from "react";
import * as R from "remeda";

import { LabelButton } from "@shared/components/Button";
import { Select, SelectLabel } from "@shared/components/form/Select";

import { StudyContextData, SubmissionContextData } from "@/context/AppContext";

import { T } from "@/components/i18n";
import { UploadInstructions } from "@/components/submission/UploadInstructions";

import { Calendar, ShieldAlert, ShieldCheck } from "lucide-react";
import { effectiveStudy } from "@shared/study";

interface SubmissionFormProps {
  studyContext: StudyContextData;
  submissionContext: SubmissionContextData;
  children: React.ReactNode;
}

export function SubmissionForm({ submissionContext, studyContext, children }: SubmissionFormProps) {
  const weekId = useId();
  const { study } = studyContext;
  const { defaultWeek, overrideDefaultWeek, setOverrideDefaultWeek, week, setWeek, validation } =
    submissionContext;

  if (!study) {
    return null;
  }

  const baselineDays = effectiveStudy(study).baselineChatHistoryWindowDays;
  const hasDefaultWeek = defaultWeek !== null && !overrideDefaultWeek;
  const showFailureDisclaimer =
    validation?.status === "rejected" &&
    validation.failure.overridable &&
    validation.failure.reason !== "low_message_volume";

  return (
    <div className="flex flex-col">
      <div className="pb-4 mb-4 border-b border-neutral-200 dark:border-neutral-800">
        <UploadInstructions />
        {baselineDays && (
          <div className="mt-2 text-sm flex items-center gap-x-0.5 text-neutral-500">
            {showFailureDisclaimer ? (
              <>
                <ShieldAlert size={14} className="stroke-neutral-500 -mt-0.5" />
                <T i18nKey="view.submission.form.failureDisclaimer" count={baselineDays} />
              </>
            ) : (
              <>
                <ShieldCheck size={14} className="stroke-neutral-500 -mt-0.5" />
                <T i18nKey="view.submission.form.disclaimer" count={baselineDays} />
              </>
            )}
          </div>
        )}
      </div>
      {hasDefaultWeek && (
        <div className="flex gap-x-2 items-center pb-4">
          <Calendar size={16} className="stroke-blue-500 -mt-0.5" />
          <span className="font-semibold">Study week {defaultWeek}</span>
          <LabelButton className="-mt-0.5" onClick={() => setOverrideDefaultWeek(true)}>
            Change
          </LabelButton>
        </div>
      )}
      {!hasDefaultWeek && (
        <div className="mb-6 w-fit">
          <SelectLabel htmlFor={weekId}>
            <Calendar size={16} className="stroke-blue-500 -mt-0.5" />
            Select study week
            {overrideDefaultWeek && (
              <LabelButton
                className="-mt-0.5"
                onClick={() => {
                  setOverrideDefaultWeek(false);
                  setWeek(null);
                }}
              >
                Reset
              </LabelButton>
            )}
          </SelectLabel>
          <div className="flex items-center gap-x-3">
            <Select
              onChange={(e) => setWeek(parseInt(e.currentTarget.value, 10))}
              value={week}
              name="week"
              id={weekId}
            >
              <option value="" disabled>
                Select week
              </option>
              {R.pipe(
                [1, 2, 4, 8, 12],
                R.map((i) => (
                  <option key={i} value={i}>
                    Week {i}
                  </option>
                )),
              )}
            </Select>
          </div>
        </div>
      )}
      {children}
    </div>
  );
}
